<p-confirmDialog [style]="{width: '480px'}"></p-confirmDialog>

<p-dialog
  [(visible)]="visible"
  (visibleChange)="visibleChange.emit($event)"
  [modal]="true"
  [style]="{width: '95vw', maxWidth: '1400px', height: '90vh'}"
  [contentStyle]="{height: 'calc(90vh - 64px)', overflow: 'auto', padding: '0 1.25rem 1rem'}"
  header="NACHA Tools — Tax & Owner Distribution Files"
  (onHide)="close()">

  <!-- Period banner -->
  <div class="nacha-banner">
    <div>
      <strong>Period:</strong>
      {{ month }}/{{ year }}
      &nbsp;·&nbsp;
      <span class="muted">Files include statements regardless of status — safe for testing before publish.</span>
    </div>
    <button pButton type="button" icon="pi pi-refresh" label="Reload"
            class="p-button-text p-button-sm"
            (click)="activeTab === 'tax' ? loadTaxPreview() : loadDistPreview(); loadBatches()"></button>
  </div>

  <!-- Tab nav -->
  <div class="nacha-tabs">
    <button pButton type="button"
            [label]="'Tax NACHA (GET / TAT / MCTAT)'"
            icon="pi pi-percentage"
            class="p-button-sm"
            [ngClass]="activeTab === 'tax' ? 'p-button-primary' : 'p-button-outlined'"
            (click)="switchTab('tax')"></button>
    <button pButton type="button"
            label="Owner Distribution (BoH ePay)"
            icon="pi pi-money-bill"
            class="p-button-sm"
            [ngClass]="activeTab === 'dist' ? 'p-button-primary' : 'p-button-outlined'"
            (click)="switchTab('dist')"></button>
  </div>

  <div *ngIf="loading" class="nacha-empty">
    <i class="pi pi-spin pi-spinner" style="font-size:1.5rem;margin-right:8px"></i>
    Loading preview…
  </div>

  <div *ngIf="error" class="nacha-error">
    <i class="pi pi-exclamation-triangle" style="margin-right:6px"></i>{{ error }}
  </div>

  <!-- ============================================================== -->
  <!-- TAX TAB                                                         -->
  <!-- ============================================================== -->
  <div *ngIf="activeTab === 'tax' && !loading" class="nacha-section">

    <div class="nacha-controls">
      <label>Tax Type:</label>
      <p-selectButton
        [options]="[{label:'GET', value:'GET'},{label:'TAT', value:'TAT'},{label:'MCTAT', value:'MCTAT'}]"
        [(ngModel)]="taxType"
        (onChange)="onTaxTypeChange()"></p-selectButton>

      <span class="nacha-summary">
        <strong>{{ taxSelectedCount() }}</strong> selected
        &nbsp;·&nbsp;
        <strong>{{ formatMoney(taxSelectedTotal()) }}</strong>
      </span>

      <span class="nacha-spacer"></span>

      <button pButton type="button" label="Export to Excel"
              icon="pi pi-file-excel"
              class="p-button-outlined p-button-sm"
              [disabled]="taxRows.length === 0"
              (click)="exportTaxToExcel()"></button>

      <button pButton type="button" label="Generate {{ taxType }} File"
              icon="pi pi-download"
              class="p-button-success p-button-sm"
              [disabled]="taxSelectedCount() === 0 || generating"
              (click)="generateTaxFile()"></button>
    </div>

    <p-table [value]="taxRows" [paginator]="false"
             [scrollable]="true" scrollHeight="50vh">
      <ng-template pTemplate="header">
        <tr>
          <th style="width:42px">
            <p-checkbox [binary]="true" [ngModel]="false"
                        (onChange)="selectAllTax($event.checked)"
                        pTooltip="Select all eligible"></p-checkbox>
          </th>
          <th>Property</th>
          <th>Owner</th>
          <th>Tax ID</th>
          <th>County</th>
          <th class="num">GET</th>
          <th class="num">TAT</th>
          <th class="num">MCTAT</th>
          <th>Already Batched ({{ taxType }})</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr [ngClass]="{'row-batched': taxBatched(r)}">
          <td>
            <p-checkbox [binary]="true" [(ngModel)]="r.selected"
                        [disabled]="!taxAmount(r) || taxAmount(r) === 0 || taxBatched(r)"></p-checkbox>
          </td>
          <td>
            <a *ngIf="r.ownerStmtId; else justCode"
               [href]="'/owner-stmt-preview/' + r.ownerStmtId"
               target="_blank"
               rel="noopener"
               class="property-code-link"
               pTooltip="Open the full owner statement in a new tab"
               tooltipPosition="right">
              {{ r.propertyCode }}
              <i class="pi pi-external-link" style="font-size:0.7rem;margin-left:0.2rem;opacity:0.6"></i>
            </a>
            <ng-template #justCode>{{ r.propertyCode }}</ng-template>
          </td>
          <td>{{ r.ownerName }}</td>
          <td>{{ r.rentalTaxIdNumber || '—' }}</td>
          <td>{{ r.addressCounty || '—' }}</td>
          <td class="num" [class.dim]="!r.getAmount">{{ formatMoney(r.getAmount) }}</td>
          <td class="num" [class.dim]="!r.tatAmount">{{ formatMoney(r.tatAmount) }}</td>
          <td class="num" [class.dim]="!r.mctatAmount">{{ formatMoney(r.mctatAmount) }}</td>
          <td>
            <span *ngIf="taxBatched(r)" class="batched-pill">
              Batch #{{ taxBatched(r) }}
            </span>
            <span *ngIf="!taxBatched(r)" class="muted">—</span>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="9" style="text-align:center;color:#888;padding:2rem">
          No statements found for {{ month }}/{{ year }}.
        </td></tr>
      </ng-template>
    </p-table>
  </div>

  <!-- ============================================================== -->
  <!-- DISTRIBUTION TAB                                                -->
  <!-- ============================================================== -->
  <div *ngIf="activeTab === 'dist' && !loading" class="nacha-section">

    <div class="nacha-controls">
      <label>Effective Date (BoH settlement):</label>
      <p-calendar
        [(ngModel)]="effectiveDateObj"
        (onSelect)="onEffectiveDateChange()"
        (onBlur)="onEffectiveDateChange()"
        [showIcon]="true"
        dateFormat="mm/dd/yy"
        [readonlyInput]="false"
        appendTo="body">
      </p-calendar>
      <span class="muted" *ngIf="effectiveDate">YYMMDD: {{ effectiveDate }}</span>

      <span class="nacha-summary">
        <strong>{{ distSelectedCount() }}</strong> selected
        &nbsp;·&nbsp;
        <strong>{{ formatMoney(distSelectedTotal()) }}</strong>
      </span>

      <span class="nacha-spacer"></span>

      <button pButton type="button" label="Generate Distribution File"
              icon="pi pi-download"
              class="p-button-success p-button-sm"
              [disabled]="distSelectedCount() === 0 || generating || !effectiveDate"
              (click)="generateDistFile()"></button>

      <button pButton type="button" icon="pi pi-info-circle"
              class="p-button-text p-button-sm dist-help-btn"
              pTooltip="What do these statuses mean? Click for legend."
              tooltipPosition="left"
              (click)="showDistHelp = !showDistHelp"></button>
    </div>

    <!-- Legend panel — toggled by the (i) help button above -->
    <div *ngIf="showDistHelp" class="dist-help-panel">
      <div class="dist-help-header">
        <strong>Owner Distribution preview — legend</strong>
        <button pButton type="button" icon="pi pi-times"
                class="p-button-text p-button-sm"
                (click)="showDistHelp = false"></button>
      </div>

      <table class="dist-help-table">
        <tr>
          <th colspan="2">Row colors</th>
        </tr>
        <tr>
          <td><span class="legend-swatch legend-eligible"></span> White (no shade)</td>
          <td><strong>Eligible &mdash; selectable.</strong>
            Statement has valid bank info, a positive payment-to-owner amount, and is NOT
            yet part of a NACHA batch. Tick the checkbox to include it in the next batch.</td>
        </tr>
        <tr>
          <td><span class="legend-swatch legend-batched"></span> Gray / faded</td>
          <td><strong>Already batched.</strong>
            The row shows a "Batch #N" pill, meaning a NACHA file has already been generated
            for this statement. Checkbox is disabled. To free it for a NEW batch, click the
            red &times; next to the Batch #N pill (server refuses if that batch is SUBMITTED).</td>
        </tr>
        <tr>
          <td><span class="legend-swatch legend-warn"></span> Yellow</td>
          <td><strong>Missing bank info &mdash; not selectable.</strong>
            The owner's <code>bankRoutingNum</code> and/or <code>bankAccountNum</code> on
            <code>str_property_profile</code> is NULL, so we can't credit them via ACH.
            Routing &amp; Account columns show <span class="warn-text">missing</span>.
            Fix in Property Profile &rarr; Bank Info, then reload this dialog.</td>
        </tr>
        <tr>
          <th colspan="2">Column meanings</th>
        </tr>
        <tr>
          <td><strong>Property</strong></td>
          <td>Click the property code to open the full owner statement in a new tab.</td>
        </tr>
        <tr>
          <td><strong>Bank</strong></td>
          <td>Owner's destination bank name (where THIS owner gets paid). Some rows show
            &mdash; when the bank name isn't recorded on the profile (the routing/account
            still work without the friendly name).</td>
        </tr>
        <tr>
          <td><strong>Routing / Account</strong></td>
          <td>Owner's ABA routing number + bank account number. <span class="warn-text">missing</span>
            means the column is NULL on <code>str_property_profile</code>.</td>
        </tr>
        <tr>
          <td><strong>Distribution</strong></td>
          <td>The dollar amount we owe THIS owner this cycle &mdash; this is the same number
            as the "Payment to Owner" line on the rendered PDF statement. $0.00 rows are NOT
            selectable (nothing to wire).</td>
        </tr>
        <tr>
          <td><strong>Already Batched</strong></td>
          <td>If populated, the NACHA batch ID this statement is currently linked to. The
            red &times; next to the pill deletes that batch and frees this statement for
            re-batching (refused server-side if the batch is SUBMITTED).</td>
        </tr>
        <tr>
          <th colspan="2">Why a statement isn't selectable</th>
        </tr>
        <tr>
          <td>Checkbox disabled because&hellip;</td>
          <td>Any one of: (a) distribution amount is $0 or negative, (b) already in a batch
            (gray row), or (c) missing bank info (yellow row).</td>
        </tr>
      </table>
    </div>

    <p-table [value]="distRows" [paginator]="false"
             [scrollable]="true" scrollHeight="60vh">
      <ng-template pTemplate="header">
        <tr>
          <th style="width:42px">
            <p-checkbox [binary]="true" [ngModel]="false"
                        (onChange)="selectAllDist($event.checked)"
                        pTooltip="Select all eligible"></p-checkbox>
          </th>
          <th>Property</th>
          <th>Owner</th>
          <th>Bank</th>
          <th>Routing</th>
          <th>Account</th>
          <th class="num">Distribution</th>
          <th>Already Batched</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-r>
        <tr [ngClass]="{'row-batched': r.nachaDistBatchId, 'row-warn': !hasBankInfo(r)}">
          <td>
            <p-checkbox [binary]="true" [(ngModel)]="r.selected"
                        [disabled]="!r.distributionAmount || r.distributionAmount <= 0 || r.nachaDistBatchId || !hasBankInfo(r)"></p-checkbox>
          </td>
          <td>
            <a *ngIf="r.ownerStmtId; else justCode"
               [href]="'/owner-stmt-preview/' + r.ownerStmtId"
               target="_blank"
               rel="noopener"
               class="property-code-link"
               pTooltip="Open the full owner statement in a new tab"
               tooltipPosition="right">
              {{ r.propertyCode }}
              <i class="pi pi-external-link" style="font-size:0.7rem;margin-left:0.2rem;opacity:0.6"></i>
            </a>
            <ng-template #justCode>{{ r.propertyCode }}</ng-template>
          </td>
          <td>{{ r.ownerName }}</td>
          <td>{{ r.bankName || '—' }}</td>
          <td>
            <span *ngIf="r.bankRoutingNum">{{ r.bankRoutingNum }}</span>
            <span *ngIf="!r.bankRoutingNum" class="warn-text">missing</span>
          </td>
          <td>
            <span *ngIf="r.bankAccountNum">{{ r.bankAccountNum }}</span>
            <span *ngIf="!r.bankAccountNum" class="warn-text">missing</span>
          </td>
          <td class="num">{{ formatMoney(r.distributionAmount) }}</td>
          <td>
            <span *ngIf="r.nachaDistBatchId" class="batched-pill">
              Batch #{{ r.nachaDistBatchId }}
            </span>
            <button *ngIf="r.nachaDistBatchId" pButton type="button"
                    icon="pi pi-times" class="p-button-text p-button-sm p-button-danger"
                    pTooltip="Delete this batch + free up this property for a new batch"
                    tooltipPosition="left"
                    style="padding:0.15rem 0.3rem;margin-left:0.35rem;font-size:0.75rem"
                    (click)="deleteDistBatchInline(r)"></button>
            <span *ngIf="!r.nachaDistBatchId" class="muted">—</span>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="8" style="text-align:center;color:#888;padding:2rem">
          No statements found for {{ month }}/{{ year }}.
        </td></tr>
      </ng-template>
    </p-table>
  </div>

  <!-- ============================================================== -->
  <!-- BATCH HISTORY                                                   -->
  <!-- ============================================================== -->
  <div class="nacha-history">
    <h4>Recent NACHA Batches</h4>

    <div class="nacha-history-grid">
      <div>
        <h5>Tax Batches (State of Hawaii)</h5>
        <p-table [value]="taxBatches" [rows]="10" [paginator]="taxBatches.length > 10">
          <ng-template pTemplate="header">
            <tr>
              <th>#</th><th>Type</th><th>Period</th><th>Entries</th>
              <th>Status</th><th>Generated</th><th>By</th><th>File</th><th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-b>
            <tr>
              <td>{{ b.nachaBatchId }}</td>
              <td>{{ b.taxType }}</td>
              <td>{{ b.periodStart }} → {{ b.periodEnd }}</td>
              <td>{{ b.entryCount }}</td>
              <td>
                <span [ngClass]="b.status === 'SUBMITTED' ? 'pill-green' : 'pill-yellow'">
                  {{ b.status }}
                </span>
              </td>
              <td>{{ formatDate(b.generatedAt) }}</td>
              <td class="muted">{{ b.generatedBy }}</td>
              <td class="muted small">{{ b.nachaFileName }}</td>
              <td>
                <button *ngIf="b.status !== 'SUBMITTED'"
                        pButton type="button" icon="pi pi-check" label="Mark Submitted"
                        class="p-button-text p-button-sm"
                        (click)="markTaxBatchSubmitted(b)"></button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="9" class="muted" style="text-align:center">No tax batches yet.</td></tr>
          </ng-template>
        </p-table>
      </div>

      <div>
        <h5>Distribution Batches (Bank of Hawaii ePay)</h5>
        <p-table [value]="distBatches" [rows]="10" [paginator]="distBatches.length > 10">
          <ng-template pTemplate="header">
            <tr>
              <th>#</th><th>Period</th><th>Entries</th><th class="num">Total</th>
              <th>Status</th><th>Generated</th><th>By</th><th>File</th><th></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-b>
            <tr>
              <td>{{ b.nachaDistBatchId }}</td>
              <td>{{ b.periodStart }} → {{ b.periodEnd }}</td>
              <td>{{ b.entryCount }}</td>
              <td class="num">{{ formatMoney(b.totalAmount) }}</td>
              <td>
                <span [ngClass]="b.status === 'SUBMITTED' ? 'pill-green' : 'pill-yellow'">
                  {{ b.status }}
                </span>
              </td>
              <td>{{ formatDate(b.generatedAt) }}</td>
              <td class="muted">{{ b.generatedBy }}</td>
              <td class="muted small">{{ b.nachaFileName }}</td>
              <td>
                <button *ngIf="b.status !== 'SUBMITTED'"
                        pButton type="button" icon="pi pi-check" label="Mark Submitted"
                        class="p-button-text p-button-sm"
                        (click)="markDistBatchSubmitted(b)"></button>
                <button *ngIf="b.status !== 'SUBMITTED'"
                        pButton type="button" icon="pi pi-trash" label="Delete"
                        class="p-button-text p-button-sm p-button-danger"
                        pTooltip="Delete batch + unlink statements so they can be re-batched"
                        (click)="deleteDistBatch(b)"></button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="9" class="muted" style="text-align:center">No distribution batches yet.</td></tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  </div>

  <ng-template pTemplate="footer">
    <button pButton type="button" label="Close" icon="pi pi-times"
            class="p-button-text" (click)="close()"></button>
  </ng-template>
</p-dialog>
